import React, { useMemo } from 'react';
import { flex } from '@fabric-ui/theme';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Skeleton, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { DwQueryRequest } from '../../CustomDashboards/types';
import { calculatePercentageChange, getNumericValue } from '../utils';
import AgentTileHeading from './AgentTileHeading';
import { getTotalAlertsCountByAgents } from './queries';
import StatusChange from './statusChange';
import { getLast24HoursDate } from './utils';

const AgentStatus = ({ filterContext, totalAlertCount }) => {
  const last24HoursDate = useMemo(() => getLast24HoursDate(filterContext.dateRange[1]), [filterContext.dateRange]);
  const prev24HoursDate = useMemo(() => getLast24HoursDate(last24HoursDate), [last24HoursDate]);

  const last24HoursQueryObject = useMemo(
    () => [
      getTotalAlertsCountByAgents({
        ...filterContext,
        dateRange: [last24HoursDate, filterContext.dateRange[1]],
      }) as DwQueryRequest,
    ],
    [filterContext, last24HoursDate]
  );

  const prev24HoursQueryObject = useMemo(
    () => [
      getTotalAlertsCountByAgents({
        ...filterContext,
        dateRange: [prev24HoursDate, last24HoursDate],
      }) as DwQueryRequest,
    ],
    [filterContext, prev24HoursDate, last24HoursDate]
  );

  const last24HoursAlertCount = useQueryObject({
    queryObject: last24HoursQueryObject,
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data?.[0]?.cnt,
  });

  const previous24HoursAlertCount = useQueryObject({
    queryObject: prev24HoursQueryObject,
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data?.[0]?.cnt,
  });

  const percentage = useMemo(
    () => calculatePercentageChange(getNumericValue(previous24HoursAlertCount.data), getNumericValue(last24HoursAlertCount.data)),
    [previous24HoursAlertCount.data, last24HoursAlertCount.data]
  );

  return (
    <Box sx={{ padding: '16px 24px', background: 'var(--color-surface-base-primary)', minHeight: '137px', height: '100%', ...flex.col }}>
      <AgentTileHeading
        title="Alerts Investigated"
        infoMessage="Alerts analyzed by agents in your selected time range. Trend = last 24h vs. previous 24h."
      />
      {!totalAlertCount.isLoading && !totalAlertCount.isRefetching ? (
        <div>
          <Box display="flex" flexDirection="row" alignItems="baseline" gap="8px">
            <Typography variant="h3" sx={{ m: 0, fontSize: '24px', color: 'var(--color-content-base-primary)' }}>
              {Number(totalAlertCount?.data || 0).toLocaleString() ?? 0}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'var(--color-content-base-primary)' }}>
              Total
            </Typography>
          </Box>
          <Box sx={{ ...flex.itemsCenter, gap: 1, lineHeight: '18px', marginTop: '8px' }}>
            <StatusChange value={percentage} toFixed={0} />
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'var(--color-content-base-subdued)' }}>
              Last 24 hours
            </Typography>
          </Box>
        </div>
      ) : (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      )}
    </Box>
  );
};

export default AgentStatus;
